import { createReducer, on } from "@ngrx/store";
import { TwoFactorAuthMethod } from "@obg/ngx.core";

import { TwoFactorAuthAction } from "./two-factor-auth.action";
import {
	CommunicationChannel,
	TwoFactorAuthCodeBEVerificationErrors,
	TwoFactorAuthCodeCommonVerificationErrors,
	TwoFactorChallengeType,
} from "./two-factor-auth.const";
import { TwoFactorAuthState } from "./two-factor-auth.model";

// TODO: AOCC-11506 There's a lot of stuff here that don't
// need to be part of the global state
const INITIAL_STATE: TwoFactorAuthState = {
	isBusy: false,
	errorCode: undefined,
	oneTimePassword: undefined,
	selectedMethod: undefined,
	enabledMethods: [],
	successOperationType: undefined,
	status: undefined,
	tokenValidityLengthInMinutes: undefined,
	tokenValidUntil: undefined,
	isLoginPasswordVerified: false,
	loginToken: undefined,
	password: undefined,
	passwordErrorCode: undefined,
	challengeDetails: undefined,
	currentDevice: undefined,
	remainingRequests: undefined,
	remainingAttempts: 5,
	isPhoneNumberVerified: false,
	isEmailVerified: false,
};

export const twoFactorAuthReducer = createReducer(
	INITIAL_STATE,
	on(TwoFactorAuthAction.requestOTP, TwoFactorAuthAction.retryRequestOTP, (state) => ({
		...state,
	})),
	on(TwoFactorAuthAction.updateChallengeDetails, (state, { challengeDetails, remainingAttempts }) => ({
		...state,
		challengeDetails,
		selectedMethod:
			challengeDetails.challengeType === TwoFactorChallengeType.SuspiciousLogin
				? TwoFactorAuthMethod.EmailTwoFactorAuthenticationMethod
				: challengeDetails.primaryCommunicationMethod.channel === CommunicationChannel.Email
					? TwoFactorAuthMethod.EmailTwoFactorAuthenticationMethod
					: TwoFactorAuthMethod.SmsTwoFactorAuthenticationMethod,
		remainingAttempts: Number(remainingAttempts) ?? state.remainingAttempts,
	})),
	on(
		TwoFactorAuthAction.requestOTPSuccess,
		(state, { payload: { tokenValidUntil, tokenValidityLengthInMinutes, remainingRequests, status } }) => ({
			...state,
			tokenValidityLengthInMinutes,
			tokenValidUntil,
			remainingRequests,
			status,
			remainingAttempts: INITIAL_STATE.remainingAttempts,
		})
	),
	on(TwoFactorAuthAction.verifyOTP, TwoFactorAuthAction.verifyOTPAccountNotVerified, (state) => ({
		...state,
		isBusy: true,
		errorCode: undefined,
	})),
	on(TwoFactorAuthAction.verifyOTPSuccess, (state) => ({
		...state,
		isBusy: false,
		errorCode: undefined,
	})),
	on(TwoFactorAuthAction.requestOTPFailure, TwoFactorAuthAction.verifyOTPFailure, (state, { errorCode }) => ({
		...state,
		isBusy: false,
		errorCode,
	})),
	on(TwoFactorAuthAction.verifyOTPAccountNotVerifiedFailure, (state, { errorCode }) => {
		const remainingAttempts = state.remainingAttempts || state.challengeDetails?.remainingAttempts;
		return {
			...state,
			isBusy: false,
			errorCode,
			remainingAttempts: Math.max((remainingAttempts || 0) - 1, 0),
		};
	}),
	on(TwoFactorAuthAction.setDefaultVerifyLoginPasswordState, (state) => ({
		...state,
		isLoginPasswordVerified: false,
		passwordErrorCode: undefined,
	})),
	on(TwoFactorAuthAction.setEnabledMethods, (state, { methods }) => ({ ...state, enabledMethods: methods })),
	on(TwoFactorAuthAction.setSelectedMethod, (state, { method }) => ({ ...state, selectedMethod: method })),
	on(TwoFactorAuthAction.operateAuthMethod, (state) => ({
		...state,
		isBusy: true,
		successOperationType: undefined,
	})),
	on(TwoFactorAuthAction.operateAuthMethodSuccess, (state, { response, operationType }) => ({
		...state,
		isBusy: false,
		selectedMethod: undefined,
		enabledMethods: response,
		successOperationType: operationType,
		errorCode: undefined,
	})),
	on(TwoFactorAuthAction.operateAuthMethodFailure, (state, { errorCode, remainingAttempts }) => ({
		...state,
		isBusy: false,
		errorCode: remainingAttempts
			? errorCode
			: TwoFactorAuthCodeBEVerificationErrors.E_AUTHENTICATIONCHALLENGES_MAXATTEMPTSREACHED,
		remainingAttempts:
			errorCode === TwoFactorAuthCodeBEVerificationErrors.E_AUTHENTICATIONCHALLENGES_MAXATTEMPTSREACHED
				? 0
				: remainingAttempts,
	})),
	on(TwoFactorAuthAction.verifyLoginPassword, (state, { password }) => ({
		...state,
		isBusy: true,
		isLoginPasswordVerified: false,
		passwordErrorCode: undefined,
		password,
	})),
	on(TwoFactorAuthAction.verifyLoginPasswordSuccess, (state, { response }) => ({
		...state,
		isBusy: false,
		isLoginPasswordVerified: !!response,
		loginToken: response.verificationToken,
		passwordErrorCode: undefined,
	})),
	on(TwoFactorAuthAction.verifyLoginPasswordFailure, (state, { errorCode }) => ({
		...state,
		isBusy: false,
		passwordErrorCode: errorCode,
		isLoginPasswordVerified: false,
		password: undefined,
	})),
	on(TwoFactorAuthAction.resetOnSuspiciousLogin, TwoFactorAuthAction.resetOnLogout, () => ({ ...INITIAL_STATE })),
	on(TwoFactorAuthAction.resetOperationType, (state) => ({ ...state, successOperationType: undefined })),
	on(TwoFactorAuthAction.resetErrorCode, (state) => ({ ...state, errorCode: undefined })),
	on(TwoFactorAuthAction.resetOtpExpiredErrorCode, (state) => ({
		...state,
		errorCode:
			state.errorCode === TwoFactorAuthCodeCommonVerificationErrors.ExpiredCode ? undefined : state.errorCode,
	})),
	on(TwoFactorAuthAction.resetTemporaryErrorCode, (state) => ({
		...state,
		errorCode: [
			TwoFactorAuthCodeBEVerificationErrors.E_AUTHENTICATIONCHALLENGES_MAXATTEMPTSREACHED,
			TwoFactorAuthCodeBEVerificationErrors.E_SESSIONS_LOGIN_2FA_MAXIMUM_ATTEMPTS_REACHED,
		].includes(state.errorCode as TwoFactorAuthCodeBEVerificationErrors)
			? state.errorCode
			: undefined,
	})),
	on(TwoFactorAuthAction.toggleSelectedMethod, (state) => ({
		...state,
		selectedMethod:
			state.selectedMethod === TwoFactorAuthMethod.EmailTwoFactorAuthenticationMethod
				? TwoFactorAuthMethod.SmsTwoFactorAuthenticationMethod
				: TwoFactorAuthMethod.EmailTwoFactorAuthenticationMethod,
	})),
	on(TwoFactorAuthAction.setCurrentDevice, (state, { currentDevice }) => ({
		...state,
		currentDevice,
	})),
	on(TwoFactorAuthAction.enableCurrentDevice, (state, { isEnabled }) => ({
		...state,
		currentDevice: {
			...state.currentDevice!,
			isEnabledRememberDevice: isEnabled,
		},
	})),
	on(TwoFactorAuthAction.forgetDevice, (state) => ({
		...state,
		isBusy: true,
	})),
	on(TwoFactorAuthAction.forgetDeviceSuccess, (state) => ({
		...state,
		isBusy: false,
		currentDevice: {
			...state.currentDevice!,
			isEnabledRememberDevice: false,
		},
	})),
	on(TwoFactorAuthAction.deviceFailure, (state, { errorCode }) => ({
		...state,
		isBusy: false,
		errorCode,
	})),
	on(TwoFactorAuthAction.setIsBusy, (state, { isBusy }) => ({
		...state,
		isBusy,
	}))
);
